import type { HttpResourceRef } from '@angular/common/http';
import { effect, type Signal, signal } from '@angular/core';
import type { GuardResult } from '@angular/router';

function createResourceGuardPromise<TResult>(
  resource: HttpResourceRef<TResult>,
): Promise<GuardResult> {
  return new Promise<GuardResult>((resolve) => {
    const effectRef = effect(
      () => {
        if (resource.isLoading()) {
          return;
        }

        effectRef.destroy();

        if (resource.status() === 'error') {
          resource.destroy();
          resolve(false);
          return;
        }

        if (resource.hasValue()) {
          resource.destroy();
          resolve(true);
          return;
        }

        resource.destroy();
        resolve(false);
      },
      { manualCleanup: true },
    );
  });
}

export function resolveResourceAccessGuardResult<TResult>(
  idParams: string | string[],
  resourceFactory: (
    idSignals: Signal<string> | Signal<string>[],
  ) => HttpResourceRef<TResult>,
): GuardResult | Promise<GuardResult> {
  if (Array.isArray(idParams)) {
    if (idParams.some((v) => !v || v === '')) {
      return false;
    }

    const idSignals = idParams.map((param) => signal(param));
    const resource = resourceFactory(idSignals);
    return createResourceGuardPromise(resource);
  }

  if (!idParams || idParams === '') {
    return false;
  }

  const idSignal = signal(idParams);
  const resource = resourceFactory(idSignal);
  return createResourceGuardPromise(resource);
}
